import React, { useRef, useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withApi } from '@tencent/react-aio';
import AddBlackListModal from '@/pages/usercenter/AddBlackListModal';
import Delegate from '@/components/Delegate';
import * as ActionTypes from '@/stores/sale/actions';
import followRecordStore from '@/business-components/sale/callcenter/FollowRecord/store';
import message from '@/utils/message';

interface PhoneInfoProps {
  leadId: number;
  number: string | {
    value: string;
    values: string[];
  }; // 电话号码
  contactId: number; // 联系人id
  callCenter: any; // 呼叫中心配置
  secure: boolean; // 是否重新加密
  enabled: boolean; // 呼叫中心状态
  isTcccCall: boolean;
  statusList: Record<string, any>; // 电话状态列表
  decrypted: boolean;
  leadDetail?: any;
  webApi: {
    checkBlackListRecord: () => any;
  }
  reloadPage?: () => void;
}

const copyText = (value: string) => {
  const area = document.createElement('textarea');
  area.innerHTML = value;
  area.style.position = 'absolute';
  area.style.left = '-1000px';
  area.style.top = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand('Copy');
  document.body.removeChild(area);
  message.success('已复制号码');
};

const CopyNumber = (props: { displayNumber: number | string }) => {
  const {
    displayNumber,
  } = props;
  const num = `${displayNumber}`;
  if (num.includes('***')) {
    return null;
  }
  return <span className="number-copy" onClick={() => copyText(num)}>复制</span>;
};

const PhoneInfo = (props: PhoneInfoProps) => {
  const addBlackListModal = useRef<any>();
  const { number, contactId, callCenter, secure, enabled, statusList } = props;

  // 更新blackList
  const askBlackListCheck = (number: string, contactId: number) => {
    const data = {
      phoneNumber: number,
      contactId,
    };

    const storge = sessionStorage.getItem('hasAskBlackListCheck') || '{}';
    const parseStore = JSON.parse(storge);
    if (parseStore[`${number}-${contactId}`]) {
      return;
    }

    const { webApi } = props;
    webApi.checkBlackListRecord().dispatchJson(ActionTypes.CONTACT_PHONE_STATUS, {
      data,
    });

    parseStore[`${number}-${contactId}`] = true;
    sessionStorage.setItem('hasAskBlackListCheck', JSON.stringify(parseStore));
  };
  useEffect(() => {
    if (!number || !callCenter) return;
    if (_.isObject(number) && _.isArray(number?.values)) {
      _.map(number.values, (v) => {
        if (!statusList || !statusList[v]) {
          askBlackListCheck(v, contactId);
        }
      });
      return;
    }
    if (_.isString(number)) {
      if (!statusList || !statusList[number]) {
        askBlackListCheck(number, contactId);
      }
      return;
    }
  }, [number, callCenter]);

  const secureNumber = (number: string, secure: boolean) => {
    // 对于密文不再重复加密
    if (number.indexOf('*') >= 0) {
      if (number.indexOf('#') >= 0) {
        return number.substr(0, number.indexOf('#'));
      }
      return number;
    }
    // 按需重新加密
    if (secure) {
      const start = number.indexOf('-') > 0 ? number.indexOf('-') : Math.min(3, number.length);
      return number.substr(0, start) + number.substr(start).replace(/\d/g, '*');
    }
    return number;
  };

  const showAddBlackListModal = (number: string, displayNumber: string) => {
    addBlackListModal.current.show({
      number,
      displayNumber,
      contactId,
      callback: () => {},
    });
  };

  const reloadPage = () => {
    props.reloadPage?.();
  };

  const handleCall = (number: string, displayNumber: string) => {
    const { leadId, contactId, callCenter, enabled } = props;
    if (callCenter && enabled) {
      callCenter.dialOut({ leadId, contactId, number, leadDetail: props.leadDetail });
      followRecordStore.setEffect(reloadPage);
    }
  };

  const renderNumber = (number: string, secure: boolean, callCenter: any, enabled: boolean, key?: number) => {
    const { decrypted, statusList  } = props;
    const displayNumber = decrypted[number] || secureNumber(number, secure);

    let grayListIcon = null;
    let numberInfo = null;
    if (statusList && statusList[number] && statusList[number].status) {
      if (statusList[number].status === 'PHONE_STATUS_GRAY') {
        grayListIcon = (
          <span className="number-to-grayList disabled" title="此号码已加入黑名单">黑名单</span>
        );
        numberInfo = (
          <span className="number-to-dial disabled">
            {displayNumber} {callCenter && <i className="fas fa-phone fa-rotate-90" />}
          </span>
        );
      } else {
        grayListIcon = (
          <span className="number-to-grayList enabled" onClick={() => showAddBlackListModal(number, displayNumber)} title="点击加入黑名单">+ 黑名单</span>
        );
        numberInfo = (
          <span className="number-to-dial enabled" onClick={() => handleCall(number, displayNumber)} title="点击外呼">
            {displayNumber} {callCenter && <i className="fas fa-phone fa-rotate-90" />}
          </span>
        );
      }
    }

    if (enabled) {
      return (
        <div key={key} className="numberInfoInner">
          <div>
            {numberInfo}
            {grayListIcon}
            <CopyNumber displayNumber={displayNumber} />
          </div>
          <Delegate ref={addBlackListModal} component={AddBlackListModal} />
        </div>
      );
    }
    return (
        <div key={key} className="numberInfoInner">
          <span key={key} className="number-to-dial disabled" title="登录呼叫中心后方可拨打">
            {displayNumber} {callCenter && <i className="fas fa-phone-slash fa-rotate-90" />}
            <CopyNumber displayNumber={displayNumber} />
          </span>
        </div>
    );
  };

  if (!number) {
    return null;
  }

  if (typeof number === 'string') {
    return <span className="PhoneInfo">{renderNumber(number, secure, callCenter, enabled)}</span>;
  }
  const { value, values } = number;
  if (values) {
    return <span className="PhoneInfo">{_.map(values, (v, i) => renderNumber(v, secure, callCenter, enabled, i))}</span>;
  }
  return <span className="PhoneInfo">{renderNumber(value, secure, callCenter, enabled)}</span>;
};


function mapStateToProps(state: any) {
  return {
    callCenter: state.callCenter.callCenter,
    enabled: state.callCenter.status,
    decrypted: state.decryptedPhoneNumber,
    statusList: state.contactPhoneStatusList,
  };
}

export default connect(mapStateToProps)(withApi(PhoneInfo));

